import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app';
import { 
  initializeFaro, 
  getWebInstrumentations, 
  NavigationInstrumentation,
  FetchTransport } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing' 
import { APP_NAME } from '@/constants/shared';

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  
  // Skip initialization if collector URL is not configured
  if (!config.public.faroIngestUrl) {
    console.warn('Grafana Faro: Collector URL not configured. Skipping initialization.');
    return;
  }

  const appVersion = config.public.appVersion?.toString() || "unknown";
  const environment = config.public.environmentName?.toString() || "unknown";

  const isBatchingEnabled = process.env.NUXT_PUBLIC_IS_FARO_BATCHING_ENABLED?.toLowerCase() === "true";

  try {
    const faroClient = initializeFaro({
      app: {
        name: APP_NAME,
        version: appVersion,
      },
      transports: [
        // We need to use transport configuration to be able to set the buffer size, since the default buffer size is not enough.
        new FetchTransport({
          url: config.public.faroIngestUrl.toString(),
          bufferSize: 100, // Max number of items in the buffer
        }),
      ],
      instrumentations: [
        ...getWebInstrumentations({
          captureConsole: true,
        }),
        // Navigation instrumentation to track page views and route changes
        new NavigationInstrumentation(),
        // Tracing package to get end-to-end visibility for HTTP requests
        new TracingInstrumentation(),
      ],
      experimental: {
        trackNavigation: true,
      },
      sessionTracking: {
        enabled: true,
        persistent: true,
      },
      batching: {
        enabled: isBatchingEnabled,
        sendTimeout: Number(process.env.NUXT_PUBLIC_FARO_BATCHING_SEND_TIMEOUT) || 5000, // Send batch every 5 seconds
        itemLimit: Number(process.env.NUXT_PUBLIC_FARO_BATCHING_ITEM_LIMIT) || 50, // Or when 50 items are collected
      },
      beforeSend: (event) => {
        return event;
      },
      ignoreErrors: [
        /ResizeObserver loop limit exceeded/i,
        /Non-Error promise rejection captured/i,
      ],
    });

    if (config.public.appVersion) {
      faroClient.api?.setSession({
        attributes: {
          appVersion: appVersion,
          environment: environment,
        },
      });
    }

    return {
      provide: {
        faroClient,
      },
    };
  } 
  catch (error) {
    console.error('Failed to initialize Grafana Faro:', error);
  }
});
